/**
 * Paper Finance — the design system from templates/npo/npo-finance-preview.html.
 * Warm paper palette + forest-green accent, Instrument Sans / Geist Mono (fonts
 * linked in public/index.html). Tokens are exported so derived themes
 * (npoFinance) don't duplicate the values.
 */
import { createTheme, Theme } from "@mui/material/styles";
import type { ThemeConfig } from "./types";
import { MONO_FONT } from "@/shared/typography";

export const paperFinanceTokens = {
  bg: "#f0ede8",
  bg2: "#e8e4de",
  bg3: "#ddd9d2",
  surface: "#faf8f5",
  surface2: "#f4f1ec",
  ink: "#1a1814",
  ink2: "#4a4640",
  ink3: "#8a8680",
  ink4: "#b8b4ae",
  accent: "#2d6a4f",
  accent2: "#40916c",
  accentDark: "#1f4d39",
  accentLight: "#d8f3e8",
  accentPale: "#f0faf5",
  gold: "#b5742a",
  goldLight: "#fef3e2",
  red: "#c0392b",
  redLight: "#fdecea",
  blue: "#2563eb",
  blueLight: "#eff6ff",
  amber: "#d97706",
  amberLight: "#fffbeb",
  border: "rgba(26,24,20,0.10)",
  border2: "rgba(26,24,20,0.16)",
  shadow: "0 1px 3px rgba(26,24,20,0.08), 0 4px 16px rgba(26,24,20,0.04)",
  shadow2: "0 2px 8px rgba(26,24,20,0.12), 0 8px 32px rgba(26,24,20,0.06)",
  sans: '"Instrument Sans", system-ui, sans-serif',
  mono: MONO_FONT,
} as const;

export const createPaperFinanceTheme = (): Theme => {
  const t = paperFinanceTokens;
  return createTheme({
    palette: {
      mode: "light",
      primary: { main: t.accent, light: t.accentLight, dark: t.accentDark },
      secondary: { main: t.gold },
      background: { default: t.bg, paper: t.surface },
      text: { primary: t.ink, secondary: t.ink2, disabled: t.ink4 },
      success: { main: t.accent2 },
      warning: { main: t.amber },
      error: { main: t.red },
      info: { main: t.blue },
      divider: t.border,
    },
    typography: {
      fontFamily: t.sans,
      fontSize: 13,
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 500 },
      overline: {
        fontFamily: t.mono,
        fontSize: 10,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: t.bg },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${t.border}`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${t.border}`,
            boxShadow: t.shadow,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, padding: "7px 14px" },
          containedPrimary: {
            "&:hover": { backgroundColor: t.accent2 },
          },
          outlined: { borderColor: t.border2, color: t.ink2 },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              fontFamily: t.sans,
              fontSize: 14,
              fontWeight: 800,
              textTransform: "none",
              letterSpacing: "normal",
              color: t.ink,
              backgroundColor: t.surface2,
              "& .MuiTableSortLabel-root": {
                color: "inherit",
                fontSize: "inherit",
                fontWeight: "inherit",
                "&:hover": { color: "inherit" },
                "&.Mui-active": { color: "inherit" },
              },
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:hover": { backgroundColor: t.bg },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 20, fontWeight: 500, fontSize: 11 },
          colorSuccess: { backgroundColor: t.accentLight, color: t.accent },
          colorError: { backgroundColor: t.redLight, color: t.red },
          colorWarning: { backgroundColor: t.amberLight, color: t.amber },
          colorInfo: { backgroundColor: t.blueLight, color: t.blue },
        },
      },
      MuiTextField: {
        defaultProps: { size: "small" },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8 },
          standardError: { backgroundColor: t.redLight, color: t.red },
          standardWarning: { backgroundColor: t.amberLight, color: t.amber },
          standardInfo: { backgroundColor: t.blueLight, color: t.blue },
          standardSuccess: { backgroundColor: t.accentPale, color: t.accent },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(240,237,232,0.92)",
            backdropFilter: "blur(12px)",
            color: t.ink,
            boxShadow: "none",
            borderBottom: `1px solid ${t.border}`,
          },
        },
      },
    },
  });
};

export const paperFinanceThemeConfig: ThemeConfig = {
  id: "paper-finance",
  name: "Paper Finance",
  description:
    "Warm paper & forest-green design system from the NPO Finance preview (Instrument Sans + Geist Mono)",
  createTheme: createPaperFinanceTheme,
};
